import React, { useState, useEffect } from "react"
import DataTable from "aldoo-ra/DataTable"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import ContentEditor from "aldoo-ra/CMS/content-editor"
import TypeResolver from "aldoo-ra/CMS/type-resolver"
import Controller from "aldoo-ra/CMS/controllers/cms-controller"
import { showNotification } from "aldoo-ra/Notification"
import { api } from "aldoo-ra/API"
import { UpdateParam, DeleteParam } from "aldoo-ra/Admin/ParamIO/api"
import Value from "aldoo-ra/Value"
import { BoltIcon, EnvelopeIcon, EyeIcon, PencilIcon } from "@heroicons/react/24/outline"

export default function MailTriggers() {
  const [userProfile] = Value("user-profile")
  const [templates, setTemplates] = useState([])
  const [saving, setSaving] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [showPreview, setShowPreview] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [showEditor, setShowEditor] = useState(false)

  const { getContentType } = TypeResolver()
  const controller = Controller()

  // Wrapper function to call aldoo-mailer API with proper structure
  const getTriggers = async ({ page, itemsPerPage, search, sort }) => {
    const mailerAPI = api("aldoo-mailer")
    return await mailerAPI({
      get_triggers: {
        page,
        itemsPerPage,
        search,
        sort
      }
    })
  }

  // Fetch templates on component mount
  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      // Fetch available mail templates from CMS
      const cmsAPI = api("cms")
      const templatesResponse = await cmsAPI({
        get_content: {
          match: {
            "content.type": "MailTemplate",
            deleted: false
          },
          page: 1,
          itemsPerPage: 100,
          sort: { "content.name": 1 }
        }
      })

      if (templatesResponse.result && templatesResponse.data) {
        setTemplates(templatesResponse.data)
      }
    } catch (error) {
      console.error("Error fetching templates:", error)
      showNotification({
        type: "error",
        message: "Failed to load templates"
      })
    }
  }

  const handleTemplateChange = async (triggerId, newTemplate) => {
    setSaving(true)
    try {
      // Get author from user profile
      let author = "admin"
      if (userProfile?.email) {
        const authorName = userProfile.name || userProfile.email.split('@')[0]
        author = `${authorName} ${userProfile.email}`
      }

      // Build ParamIO address from trigger ID
      const paramAddress = `MAIL_TEMPLATE_TRIGGER_${triggerId.toUpperCase().replace(/-/g, '_')}`

      // If newTemplate is null/undefined or empty, clear the parameter (disable trigger)
      const value = newTemplate?.value || null

      const response = await UpdateParam({
        address: paramAddress,
        value, // Store content.id or null to disable
        author
      })

      if (response.result) {
        showNotification({
          type: "success",
          message: value ? "Trigger template updated successfully" : "Trigger template cleared"
        })

        // Refresh the table
        setRefreshKey(prev => prev + 1)
      } else {
        throw new Error(response.error || "Update failed")
      }
    } catch (error) {
      console.error("Error updating trigger:", error)
      showNotification({
        type: "error",
        message: `Failed to update trigger template: ${error.message || error}`
      })
    } finally {
      setSaving(false)
    }
  }

  // Transform templates into dropdown options
  const templateOptions = templates.map(template => ({
    value: template.content.id,
    label: template.content.name || template.content.id,
    _id: template._id, // Include MongoDB _id for backend
    ...template // Include full template data
  }))

  const columns = [
    {
      title: "Trigger",
      id: "name",
      headerClassName: "w-1/2",
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <BoltIcon className="w-5 h-5 text-admin_warning" />
          <div>
            <Typography 
              variant="admin_txt1" 
              className="block font-medium mb-0.5"
            >
              {value}
            </Typography>

            <Typography 
              variant="admin_txt2_dim" 
              className="block"
            >
              ID: {row.id}
            </Typography>
          </div>
        </div>
      )
    },
    {
      title: "Template",
      id: "currentTemplate",
      headerClassName: "w-1/2",
      render: (value, row) => {
        const currentTemplate = templateOptions.find(opt => opt.value === value)

        return (
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10">
              {row.currentTemplate && currentTemplate && (
                <Button
                  variant="admin_ghost"
                  onClick={() => {
                    setPreviewTemplate(currentTemplate)
                    setShowPreview(true)
                  }}
                  className="p-2"
                >
                  <EyeIcon className="w-5 h-5" />
                </Button>
              )}
            </div>
            <div className="flex-1">
              <DropdownSelect
                variant="admin"
                options={templateOptions}
                value={currentTemplate || null}
                onChange={(selected) => {
                  // DropdownSelect returns array even for single selection
                  const selectedItem = Array.isArray(selected) && selected.length > 0 ? selected[0] : selected
                  handleTemplateChange(row.id, selectedItem)
                }}
                placeholder="Select template..."
                searchEnabled={true}
                disabled={saving}
                loading={saving}
                showCheckbox={false}
                multiSelection={false}
                enableResetButton={true}
                allowEmptySelection={true}
                onItemRender={(option) => (
                  <div className="flex flex-col">
                    <span className="font-medium">{option.label}</span>
                    <span className="text-xs text-admin_text_secondary">
                      {option.value}
                    </span>
                  </div>
                )}
                onValueItemRender={(option) => option?.label || "Select template..."}
              />
            </div>
          </div>
        )
      }
    }
  ]

  return (
    <div>
      <div className="bg-admin_paper border-b border-admin_bordered p-6">
        <Typography 
          component="h1"
          variant="admin_h3"
        >
          Mail Template Triggers
        </Typography>

        <Typography 
          variant="admin_body_dim"
          className="my-0"
        >
          Configure which mail template is sent for each system event
        </Typography>
      </div>

      <div className="p-6">
        <DataTable
          apiMethod={getTriggers}
          apiDataField="triggers"
          requestFields={{}}
          localUserPreferencesKey="admin.mailTemplates.triggers"
          renders={columns}
          variant="admin_browser"
          showPagination={true}
          emptyMessage="No triggers registered"
          searchField="name id currentTemplate"
          showSearch={true}
          refreshKey={refreshKey}
          preferredHeight="70vh"
          styleOverrides={{
          table: "w-full table-fixed",
          container: "bg-admin_paper rounded-3xl p-6 pt-0 overflow-hidden",
          searchInput: "absolute top-6 right-8 text-admin_text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1"
        }}
        />

        {/* Preview Modal */}
        <Modal
          isOpen={showPreview}
          onClose={() => {
            setShowPreview(false)
            setPreviewTemplate(null)
          }}
          variant="admin"
          title={previewTemplate?.label || "Template Preview"}
          className="w-full max-w-[800px]"
          closeOnEscape={true}
          headerContent={
            <Button
              variant="admin_primary"
              onClick={() => {
                setEditingTemplate(previewTemplate)
                setShowPreview(false)
                setShowEditor(true)
              }}
              className="flex items-center gap-2 mr-12"
            >
              <PencilIcon className="w-4 h-4" />
              Edit
            </Button>
          }
        >
          <div className="p-6">
            {previewTemplate?.content?.description && (
              <div className="mb-4 p-4 bg-admin_canvas rounded-lg">
                <Typography variant="admin_txt2_dim" className="text-sm">
                  {previewTemplate.content.description}
                </Typography>
              </div>
            )}

            <div className="bg-white rounded-lg p-6 border border-admin_bordered">
              <div
                dangerouslySetInnerHTML={{ __html: previewTemplate?.content?.source || previewTemplate?.source || "No preview available" }}
                className="prose max-w-none"
              />
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                variant="admin_secondary"
                onClick={() => {
                  setShowPreview(false)
                  setPreviewTemplate(null)
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>

        {/* Editor Modal */}
        <Modal
          isOpen={showEditor && editingTemplate !== null}
          onClose={() => {
            setShowEditor(false)
            setEditingTemplate(null)
          }}
          closeOnEscape
          variant="admin"
          className="w-full sm:w-[500px] md:w-[85%] md:absolute md:top-[15px] h-full md:h-auto"
        >
          {editingTemplate && editingTemplate.content && (() => {
            // Extract content fields without the type field (like content-browser does)
            const { type, ...contentFields } = editingTemplate.content
            const fieldValues = {
              ...contentFields,
              _id: editingTemplate._id
            }

            return (
              <ContentEditor
                title={editingTemplate.label || editingTemplate.content?.name || "Edit Template"}
                contentType={getContentType("MailTemplate")}
                dataItem={editingTemplate}
                fieldValues={fieldValues}
                clearValuesAfterSave={false}
              onError={(errors) => {
                showNotification({
                  message: Object.values(errors).find((e) => e),
                  type: "error"
                })
              }}
              onSave={async (updatedContent) => {
                try {
                  // Remove _id from content if present
                  if (updatedContent._id) delete updatedContent._id

                  // Ensure the type field is preserved
                  if (!updatedContent.type && editingTemplate?.content?.type) {
                    updatedContent.type = editingTemplate.content.type
                  }

                  const payload = {
                    _id: editingTemplate._id,
                    content: updatedContent
                  }

                  // Add optional fields only if they exist
                  if (editingTemplate.createdAt) {
                    payload.createdAt = editingTemplate.createdAt
                  }
                  if (editingTemplate.updatedAt) {
                    payload.updatedAt = editingTemplate.updatedAt
                  }
                  if (editingTemplate.localization) {
                    payload.localization = editingTemplate.localization
                  }

                  const response = await controller.saveContent({
                    payload
                  })

                  if (response.result) {
                    showNotification({
                      type: "success",
                      message: "Template updated successfully"
                    })

                    // Refresh templates
                    await fetchTemplates()

                    // Close editor
                    setShowEditor(false)
                    setEditingTemplate(null)
                  } else {
                    throw new Error(response.error || "Update failed")
                  }
                } catch (error) {
                  console.error("Error updating template:", error)
                  showNotification({
                    type: "error",
                    message: `Failed to update template: ${error.message || error}`
                  })
                }
              }}
              onClickSave={() => {
                setShowEditor(false)
                setEditingTemplate(null)
              }}
            />
            )
          })()}
        </Modal>
      </div>
    </div>
  )
}
